import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';

import {
  withCombinedStyles,
  withComposedEnhancers
} from '../../utils/hocUtils';
import { withNamespaces } from 'react-i18next';
import LayoutStyles from '../../styles/layout';
import TypographyStyles from '../../styles/typography';
import SpacingStyles from '../../styles/helpers/spacing';
import ReportStyles from './Report.styles';
import classnames from 'classnames';

import { Paper, Typography, Popper, Fade, Grid } from '@material-ui/core';
import { Bar } from 'react-chartjs-2';
import LoadingOrError from '../../components/LoadingOrError';

import { factorColor } from '../../utils/colorUtils';
import moment from 'moment';
import _min from 'lodash/min';
import { getBar } from '../../components/Global/Graphs/graphUtils';

class CarrierPerformanceChart extends Component {
  state = {
    tooltipIndex: -1,
    anchorEl: undefined,
    arrowRef: null
  };

  chartRef = React.createRef();

  _showTooltip = payload => {
    if (payload.tooltipIndex === this.state.tooltipIndex) return;
    this.setState({
      tooltipIndex: payload.tooltipIndex,
      anchorEl: payload.anchorEl
    });
  };
  _hideTooltip = () => {
    this.setState({
      tooltipIndex: -1,
      anchorEl: undefined
    });
  };

  _handleArrowRef = node => {
    this.setState({
      arrowRef: node
    });
  };

  _handleTooltip = tooltipModel => {
    // hide the tooltip
    if (tooltipModel.opacity === 0) {
      this._hideTooltip();
      return;
    }

    const tooltipIndex = tooltipModel.dataPoints[0].index;
    if (tooltipIndex === this.state.tooltipIndex) return;

    const position = this.chartRef.current.chartInstance.chart.canvas.getBoundingClientRect();
    const chart = this.chartRef.current.chartInstance.chart;
    // eslint-disable-next-line prefer-const
    let { centerX, top, left, width, height } = getBar(chart, tooltipModel);

    const boundingBoxPadding = 10;
    top -= boundingBoxPadding;
    left -= boundingBoxPadding;
    width += boundingBoxPadding * 2;
    height += boundingBoxPadding * 2;
    const anchorEl = {
      clientWidth: position.width,
      clientHeight: position.height,
      getBoundingClientRect: () => ({
        top,
        left,
        right: left + width,
        bottom: top + height,
        width,
        height
      })
    };

    // set values

    this._showTooltip({
      top,
      left,
      width,
      height,
      centerX,
      anchorEl,
      tooltipIndex
    });
  };

  render() {
    // State and Props
    const { theme, classes, t, title, data, loading, error } = this.props;
    const { tooltipIndex, anchorEl, arrowRef } = this.state;
    const openTooltip = Boolean(anchorEl);
    const id = openTooltip ? 'tooltip-popper' : null;

    // Colors
    const backgroundColor = factorColor(theme.palette.primary.main, 0.5);
    const hoverBackgroundColor = theme.palette.primary.main;

    // Data setup
    const hasData = data.length > 0;
    const fromDate = _min(data.map(r => r.earliest));
    const chartData = data.map(r => r.percentOnTime);
    const labels = data.map(r => r.scac || r.carrierName);

    // JSX
    return (
      <Fragment>
        <Paper
          className={classnames(
            classes.containerPadding,
            classes.marginBottom3
          )}
        >
          <Grid container className={classes.chartHeader}>
            <Grid item xs={6}>
              <h2
                className={classnames(classes.headline6, classes.mediumWeight)}
              >
                {title}
              </h2>
            </Grid>
            <Grid item xs={6}>
              {hasData && (
                <div className={classes.since}>
                  {`${t('Since')} `}
                  {moment(fromDate).format('MMM D, YYYY')}
                </div>
              )}
            </Grid>
          </Grid>
          <div style={{ height: '300px' }}>
            <LoadingOrError loading={loading} error={error} noData={!hasData} />
            {hasData && (
              <Bar
                ref={this.chartRef}
                data={{
                  datasets: [
                    { data: chartData, backgroundColor, hoverBackgroundColor }
                  ],
                  labels
                }}
                height={100}
                width={50}
                options={{
                  animation: { duration: 0 },
                  responsive: true,
                  maintainAspectRatio: false,
                  legend: { display: false },
                  tooltips: {
                    enabled: false,
                    custom: this._handleTooltip
                  },

                  scales: {
                    xAxes: [
                      {
                        gridLines: {
                          display: false
                        }
                      }
                    ],
                    yAxes: [
                      {
                        display: true,
                        ticks: {
                          max: 100,
                          suggestedMin: Math.max(
                            0,
                            Math.min(...chartData) - 10
                          ),
                          callback: value => `${value}%`
                        }
                      }
                    ]
                  }
                }}
              />
            )}
          </div>
        </Paper>

        {openTooltip && (
          <Popper
            id={id}
            open={openTooltip}
            anchorEl={anchorEl}
            placement="top"
            className={classes.popper}
            transition
            modifiers={{
              flip: {
                enabled: true
              },
              arrow: {
                enabled: true,
                element: arrowRef
              },
              preventOverflow: {
                boundariesElement: 'scrollParent'
              }
            }}
          >
            {({ TransitionProps }) => (
              <Fade in {...TransitionProps} timeout={350}>
                <Paper className={classes.chartTooltip}>
                  <span className={classes.arrow} ref={this._handleArrowRef} />
                  <Typography component="div" className={classes.typography}>
                    <div className={classes.tooltipMain}>
                      {data[tooltipIndex].percentOnTime.toFixed(1)}%
                    </div>
                    <div className={classes.tooltipCarrier}>
                      <div>{data[tooltipIndex].carrierName}</div>
                      <div>{data[tooltipIndex].scac}</div>
                    </div>
                    <Grid container>
                      <Grid item xs={8} className={classes.tooltipDetailLabel}>
                        {t('Total:')}
                      </Grid>
                      <Grid item xs={4} className={classes.tooltipDetail}>
                        {data[tooltipIndex].totalCt}
                      </Grid>
                    </Grid>
                    <Grid container>
                      <Grid item xs={8} className={classes.tooltipDetailLabel}>
                        {t('On-Time:')}
                      </Grid>
                      <Grid item xs={4} className={classes.tooltipDetail}>
                        {data[tooltipIndex].numOnTime}
                      </Grid>
                    </Grid>
                    <Grid container>
                      <Grid item xs={8} className={classes.tooltipDetailLabel}>
                        {t('Late:')}
                      </Grid>
                      <Grid item xs={4} className={classes.tooltipDetail}>
                        {data[tooltipIndex].numLate}
                      </Grid>
                    </Grid>
                  </Typography>
                </Paper>
              </Fade>
            )}
          </Popper>
        )}
      </Fragment>
    );
  }
}

CarrierPerformanceChart.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.bool.isRequired
};

const localStyles = theme => ({
  since: {
    color: theme.typography.color.light,
    fontFamily: theme.typography.font.main,
    fontSize: '20px',
    fontWeight: 400,
    lineHeight: '24px',
    textTransform: 'uppercase',
    textAlign: 'right'
  },
  chartHeader: {
    marginBottom: theme.spacing.unit * 3
  },
  chartTooltip: {
    backgroundColor: theme.palette.primary.darkest,
    padding: theme.spacing.unit * 2
  },
  tooltipMain: {
    color: theme.palette.primary.contrastText,
    fontSize: '24px',
    lineHeight: '28px',
    fontWeight: 500,
    textAlign: 'center',
    marginBottom: theme.spacing.unit * 1
  },
  tooltipCarrier: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '16px',
    textAlign: 'center',
    marginBottom: theme.spacing.unit * 2
  },
  tooltipDetailLabel: {
    color: theme.palette.primary.contrastText,
    fontSize: '12px',
    lineHeight: '20px',
    textAlign: 'left'
  },
  tooltipDetail: {
    color: theme.palette.primary.contrastText,
    fontSize: '12px',
    lineHeight: '20px',
    textAlign: 'right'
  },
  // TODO: simplify popper arrow for reuse
  popper: {
    zIndex: 1,
    '&[x-placement*="bottom"] $arrow': {
      top: 0,
      left: 0,
      marginTop: '-0.9em',
      width: '3em',
      height: '1em',
      '&::before': {
        borderWidth: '0 1em 1em 1em',
        borderColor: `transparent transparent ${
          theme.palette.primary.darkest
        } transparent`
      }
    },
    '&[x-placement*="top"] $arrow': {
      bottom: 0,
      left: 0,
      marginBottom: '-0.9em',
      width: '3em',
      height: '1em',
      '&::before': {
        borderWidth: '1em 1em 0 1em',
        borderColor: `${
          theme.palette.primary.darkest
        } transparent transparent transparent`
      }
    },
    '&[x-placement*="right"] $arrow': {
      left: 0,
      marginLeft: '-0.9em',
      height: '3em',
      width: '1em',
      '&::before': {
        borderWidth: '1em 1em 1em 0',
        borderColor: `transparent ${
          theme.palette.primary.darkest
        } transparent transparent`
      }
    },
    '&[x-placement*="left"] $arrow': {
      right: 0,
      marginRight: '-0.9em',
      height: '3em',
      width: '1em',
      '&::before': {
        borderWidth: '1em 0 1em 1em',
        borderColor: `transparent transparent transparent ${
          theme.palette.primary.darkest
        }`
      }
    }
  },
  arrow: {
    position: 'absolute',
    fontSize: 10,
    width: '3em',
    height: '3em',
    '&::before': {
      content: '""',
      margin: 'auto',
      display: 'block',
      width: 0,
      height: 0,
      borderStyle: 'solid'
    }
  }
});

export default withComposedEnhancers(
  CarrierPerformanceChart,
  withNamespaces(),
  withCombinedStyles(
    true,
    TypographyStyles,
    SpacingStyles,
    LayoutStyles,
    ReportStyles,
    localStyles
  )
);
